import { addBreadcrumb } from 'shared/sentry';
import {
  ensureCallMediaReady,
  releaseCallMediaCaptureCache,
} from 'shared/services/call/callMediaCaptureService';
import {
  CALL_RINGTONE_VARIANT,
  startCallRingtoneLoop,
  stopCallRingtoneLoop,
  unlockCallRingtoneAudio,
} from 'shared/services/call/callRingtoneService';
import { CallErrorCode, CallState } from 'matrix-js-sdk/lib/webrtc/call';
import CallSession from './CallSession';
import { refreshTurnServersAndRestartIce } from './matrixCallIceUtils';
import {
  CALL_HYDRATE_INITIAL_INTERVAL_MS,
  CALL_HYDRATE_INITIAL_RETRIES,
  CALL_HYDRATE_TIMELINE_READY_INTERVAL_MS,
  CALL_HYDRATE_TIMELINE_READY_RETRIES,
  RINGTONE_BOOT_SETTLE_MS,
  RINGTONE_HANGUP_FOLLOW_MS,
  VOIP_TERMINATE_DEBOUNCE_MS,
} from '../matrixConstants';

const RECENTLY_TERMINATED_CALL_TTL_MS = 2 * 60 * 1000;

export default class CallManager {
  constructor({
    client,
    engine,
    optimizeAudio,
    safeHangup,
    safeReject,
    ensureCallEventHandler,
    getFailureCategory,
    callStats,
    onStateChange,
    timelineReady = false,
  }) {
    this.client = client;
    this.engine = engine;
    this.optimizeAudio = optimizeAudio;
    this.safeHangup = safeHangup;
    this.safeReject = safeReject;
    this.ensureCallEventHandler = ensureCallEventHandler;
    this.getFailureCategory = getFailureCategory;
    this.callStats = callStats;
    this.onStateChange = onStateChange;

    this.sessions = new Map();
    this.activeSession = null;
    this.incomingSession = null;
    this.callError = null;
    this.lastCallEndedReason = null;
    this.lastCallHangupParty = null;
    this.hydrateCancelled = false;
    /** Last user-driven terminate (end/reject) to coalesce rapid duplicate requests (429). */
    this._lastVoipTerminateAt = 0;
    this._lastVoipTerminateCallId = null;
    // Prevent duplicate "soft recovery" scheduling for the same call id.
    this.recoveryScheduled = new Set();
    /** @type {Map<string, ReturnType<typeof setTimeout>>} */
    this.recoveryScheduledTtlByCallId = new Map();
    // Tracks recovery ladders currently running (prevents stacking).
    this.recoveryInProgress = new Set();
    /** @type {Map<string, ReturnType<typeof setTimeout>>} */
    this.iceRefreshDebounceByCallId = new Map();
    /** Recently terminated call_ids; blocks stale incoming/hydration resurrection. */
    this.recentlyTerminatedCallIds = new Map();
    /**
     * Incoming ringtone is delayed only until this timestamp.
     * 0 when Matrix timeline is already ready (live invites must ring immediately).
     */
    this.incomingRingtoneDelayUntil = timelineReady ? 0 : Date.now() + RINGTONE_BOOT_SETTLE_MS;
    /** @type {Map<string, ReturnType<typeof setTimeout>>} */
    this.pendingIncomingRingtoneByCallId = new Map();

    // Precompute constant lowercase states to avoid repeated allocations.
    this.callStateStrings = {
      connecting: String(CallState.Connecting).toLowerCase(),
      ringing: String(CallState.Ringing).toLowerCase(),
      createAnswer: String(CallState.CreateAnswer).toLowerCase(),
      connected: String(CallState.Connected).toLowerCase(),
    };

    // States that typically indicate "not negotiated / stuck" and should
    // trigger (soft) recovery. Use SDK string forms only (e.g. create_answer).
    this.badStates = new Set([
      this.callStateStrings.connecting,
      this.callStateStrings.ringing,
      this.callStateStrings.createAnswer,
    ]);

    this.log = (...args) => {
      try {
        if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
          addBreadcrumb({
            category: 'matrix.call',
            message: args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' '),
            level: 'info',
          });
        }
      } catch (_) {}
    };

    this.init();
  }

  /**
   * @param {string} callId
   * @returns {boolean} true if this terminate should be skipped (duplicate within debounce window).
   */
  shouldSkipDuplicateTerminate(callId) {
    if (!callId) return false;
    const now = Date.now();
    if (
      this._lastVoipTerminateCallId === callId &&
      now - this._lastVoipTerminateAt < VOIP_TERMINATE_DEBOUNCE_MS
    ) {
      this.log('Skipping duplicate terminate (debounce)', { callId });
      return true;
    }
    this._lastVoipTerminateCallId = callId;
    this._lastVoipTerminateAt = now;
    return false;
  }

  clearRecovery = (callId) => {
    if (!callId) return;
    const ttl = this.recoveryScheduledTtlByCallId.get(callId);
    if (ttl) {
      clearTimeout(ttl);
      this.recoveryScheduledTtlByCallId.delete(callId);
    }
    this.recoveryScheduled.delete(callId);
    this.recoveryInProgress.delete(callId);
  };

  markCallRecentlyTerminated(callId) {
    if (!callId) return;
    this.recentlyTerminatedCallIds.set(callId, Date.now());
  }

  pruneRecentlyTerminatedCallIds() {
    const now = Date.now();
    for (const [callId, endedAt] of this.recentlyTerminatedCallIds.entries()) {
      if (now - endedAt >= RECENTLY_TERMINATED_CALL_TTL_MS) {
        this.recentlyTerminatedCallIds.delete(callId);
      }
    }
  }

  isRecentlyTerminated(callId) {
    if (!callId) return false;
    this.pruneRecentlyTerminatedCallIds();
    const endedAt = this.recentlyTerminatedCallIds.get(callId);
    if (!endedAt) return false;
    if (Date.now() - endedAt >= RECENTLY_TERMINATED_CALL_TTL_MS) {
      this.recentlyTerminatedCallIds.delete(callId);
      return false;
    }
    return true;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isSameSession(initialSession, initialCall) {
    return this.activeSession === initialSession && this.activeSession?.call === initialCall;
  }

  validateRecoveryStep(expectedCallId, initialSession, initialCall) {
    if (!this.recoveryInProgress.has(expectedCallId)) return null;
    if (!this.isSameSession(initialSession, initialCall)) {
      this.clearRecovery(expectedCallId);
      return null;
    }
    const session = this.activeSession;
    const call = session?.call;
    if (!call || call.callHasEnded?.()) {
      this.clearRecovery(expectedCallId);
      return null;
    }
    if (!session || session.cleaned) {
      this.clearRecovery(expectedCallId);
      return null;
    }
    if (expectedCallId && call.callId !== expectedCallId) {
      this.clearRecovery(expectedCallId);
      return null;
    }
    return { session, call };
  }

  /**
   * Debounced TURN refresh + ICE restart for a call (shared by recovery ladder and ICE listener).
   */
  triggerIceRestart(call) {
    const callId = call?.callId;
    if (!callId) {
      void refreshTurnServersAndRestartIce(call, this.client);
      return;
    }
    const existing = this.iceRefreshDebounceByCallId.get(callId);
    if (existing) return;
    const t = setTimeout(() => {
      this.iceRefreshDebounceByCallId.delete(callId);
      void refreshTurnServersAndRestartIce(call, this.client);
    }, 500);
    this.iceRefreshDebounceByCallId.set(callId, t);
  }

  async runRecoveryLadder(expectedCallId, initialSession, initialCall) {
    try {
      let ctx = this.validateRecoveryStep(expectedCallId, initialSession, initialCall);
      if (!ctx) return;

      const { call: activeCall } = ctx;
      const state = String(activeCall.state ?? '').toLowerCase();
      if (!this.badStates.has(state)) {
        this.clearRecovery(expectedCallId);
        return;
      }

      const signalingState = activeCall.peerConn?.signalingState;
      const iceState = activeCall.peerConn?.iceConnectionState;
      this.log('Soft recovery ladder start', {
        expectedCallId,
        state,
        signalingState: signalingState ?? null,
        iceState: iceState ?? null,
      });

      if (signalingState && signalingState !== 'stable') {
        this.log('Skip ICE recovery 1: signaling not stable', { expectedCallId, signalingState });
        this.clearRecovery(expectedCallId);
        return;
      }

      this.triggerIceRestart(activeCall);
      this.log('TURN refresh + ICE restart 1', { expectedCallId });

      await this.delay(4000);

      ctx = this.validateRecoveryStep(expectedCallId, initialSession, initialCall);
      if (!ctx) return;

      const { session: session2, call: call2 } = ctx;

      const stillState = String(call2.state ?? '').toLowerCase();
      if (stillState === 'connected') {
        this.clearRecovery(expectedCallId);
        return;
      }

      if (!this.badStates.has(stillState)) {
        this.clearRecovery(expectedCallId);
        return;
      }

      const signalingState2 = call2.peerConn?.signalingState;
      const iceState2 = call2.peerConn?.iceConnectionState;

      if (iceState2 === 'failed' || iceState2 === 'disconnected') {
        if (signalingState2 && signalingState2 !== 'stable') {
          this.log('Skip ICE recovery: signaling not stable', {
            expectedCallId,
            stillState,
            signalingState2,
            iceState2,
          });
          this.clearRecovery(expectedCallId);
          return;
        }

        this.log('ICE recovery from ice state', {
          expectedCallId,
          stillState,
          iceState2: iceState2 ?? null,
        });
        session2.attemptIceRecovery?.(call2, session2, {});
        this.clearRecovery(expectedCallId);
        return;
      }

      this.log('TURN refresh + ICE restart 2', {
        expectedCallId,
        stillState,
        signalingState2: signalingState2 ?? null,
        iceState2: iceState2 ?? null,
      });

      if (signalingState2 && signalingState2 !== 'stable') {
        this.log('Skip ICE recovery 2: signaling not stable', { expectedCallId, signalingState2 });
        this.clearRecovery(expectedCallId);
        return;
      }

      this.triggerIceRestart(call2);

      await this.delay(1500);

      try {
        ctx = this.validateRecoveryStep(expectedCallId, initialSession, initialCall);
        if (!ctx) return;

        const { session: session3, call: call3 } = ctx;

        const finalState = String(call3.state ?? '').toLowerCase();
        if (finalState !== 'connected' && this.badStates.has(finalState)) {
          const iceState3 = call3.peerConn?.iceConnectionState;
          if (iceState3 === 'failed' || iceState3 === 'disconnected' || !iceState3) {
            this.log('ICE fallback', { expectedCallId, finalState, iceState: iceState3 ?? null });
            session3.attemptIceRecovery?.(call3, session3, {});
          } else {
            this.log('Skip ICE fallback: ICE not failed/disconnected', {
              expectedCallId,
              finalState,
              iceState: iceState3,
            });
            this.clearRecovery(expectedCallId);
            return;
          }
        }
      } catch (err) {
        this.log('Recovery ladder step 3 error', err);
      } finally {
        this.clearRecovery(expectedCallId);
      }
    } catch (err) {
      this.log('Recovery ladder error', err);
      this.clearRecovery(expectedCallId);
    }
  }

  init() {
    this.client?.on?.('Call.incoming', this.handleIncoming);
    this.hydrateCancelled = false;
    stopCallRingtoneLoop({ restoreSession: false });
    void this.hydrateWithRetry(CALL_HYDRATE_INITIAL_RETRIES, CALL_HYDRATE_INITIAL_INTERVAL_MS);
  }

  /**
   * When Matrix reports timeline ready (including after sync recovery), pick up invites
   * that arrived slightly before or without a reliable Call.incoming to our listener.
   */
  retryHydrateAfterTimelineReady() {
    if (this.hydrateCancelled) return;
    this.markIncomingRingtoneCatchupComplete();
    if (this.activeSession || this.incomingSession) return;
    this.hydrateExistingCalls();
    void this.hydrateWithRetry(CALL_HYDRATE_TIMELINE_READY_RETRIES, CALL_HYDRATE_TIMELINE_READY_INTERVAL_MS);
  }

  async hydrateWithRetry(retries = CALL_HYDRATE_INITIAL_RETRIES, intervalMs = CALL_HYDRATE_INITIAL_INTERVAL_MS) {
    for (let i = 0; i < retries; i++) {
      if (this.hydrateCancelled) return;
      const calls = this.getLiveCallsFromClient();
      if (calls.length) {
        this.hydrateExistingCalls();
        return;
      }
      await this.delay(intervalMs);
    }
  }

  scheduleSoftRecovery = (expectedCallId, delay = 1500) => {
    if (!expectedCallId) return;
    if (this.recoveryScheduled.has(expectedCallId)) return;
    if (this.recoveryInProgress.has(expectedCallId)) return;
    this.recoveryScheduled.add(expectedCallId);
    this.recoveryInProgress.add(expectedCallId);

    const prevTtl = this.recoveryScheduledTtlByCallId.get(expectedCallId);
    if (prevTtl) clearTimeout(prevTtl);
    const ttlId = setTimeout(() => {
      this.recoveryScheduled.delete(expectedCallId);
      this.recoveryScheduledTtlByCallId.delete(expectedCallId);
    }, 30000);
    this.recoveryScheduledTtlByCallId.set(expectedCallId, ttlId);

    const initialSession = this.activeSession;
    const initialCall = initialSession?.call;

    setTimeout(() => {
      try {
        if (!this.recoveryInProgress.has(expectedCallId)) return;
        if (!this.isSameSession(initialSession, initialCall)) {
          this.clearRecovery(expectedCallId);
          return;
        }

        void this.runRecoveryLadder(expectedCallId, initialSession, initialCall).catch((err) => {
          this.log('Recovery ladder promise rejection', err);
          this.clearRecovery(expectedCallId);
        });
      } catch (err) {
        this.log('scheduleSoftRecovery error', err);
        this.clearRecovery(expectedCallId);
      }
    }, delay);
  };

  getLiveCallsFromClient() {
    const callsContainer =
      this.client?.callEventHandler?.calls || this.client?.callEventHandler?._calls || null;
    if (!callsContainer || typeof callsContainer.values !== 'function') return [];
    try {
      return Array.from(callsContainer.values()).filter((call) => {
        if (!call) return false;
        if (this.isRecentlyTerminated(call?.callId)) return false;
        try {
          return !call.callHasEnded?.();
        } catch (err) {
          this.log('getLiveCallsFromClient filter', err);
          return true;
        }
      });
    } catch (err) {
      this.log('getLiveCallsFromClient', err);
      return [];
    }
  }

  hydrateExistingCalls() {
    if (this.activeSession || this.incomingSession) return;

    const liveCalls = this.getLiveCallsFromClient();
    if (!liveCalls.length) return;

    const rank = (call) => {
      const state = String(call?.state || '').toLowerCase();
      if (state === 'connected') return 0;
      if (state === 'connecting') return 1;
      if (state === 'create_answer') return 2;
      if (state === this.callStateStrings.createAnswer) return 2;
      if (state === 'ringing') return 3;
      if (state === this.callStateStrings.ringing) return 3;
      return 4;
    };

    const call = [...liveCalls].sort((a, b) => rank(a) - rank(b))[0];

    const startWatchdog = (watchCall, timeout = 8000) => {
      const callId = watchCall?.callId;
      if (!callId) return;

      setTimeout(() => {
        try {
          const session = this.activeSession;
          const activeCall = session?.call;

          if (!activeCall || activeCall.callHasEnded?.()) return;
          if (activeCall.callId !== callId) return;

          const state = String(activeCall.state ?? '').toLowerCase();
          if (state !== 'connected') {
            this.log('Watchdog triggered recovery', { callId, state });
            this.scheduleSoftRecovery(callId, 0);
          }
        } catch (err) {
          this.log('Watchdog', err);
        }
      }, timeout);
    };

    this.lastCallEndedReason = null;
    this.lastCallHangupParty = null;
    this.callError = null;

    const state = String(call?.state ?? '').toLowerCase();

    const ringingLower = this.callStateStrings.ringing;
    const createAnswerLower = this.callStateStrings.createAnswer;
    const connectingLower = this.callStateStrings.connecting;
    const connectedLower = this.callStateStrings.connected;

    const isRingingState = state === 'ringing' || state === ringingLower;
    const isCreateAnswerState = state === 'create_answer' || state === createAnswerLower;
    const isConnectingState = state === 'connecting' || state === connectingLower;
    const isConnectedState = state === 'connected' || state === connectedLower;
    const isOngoingState =
      isConnectingState || isConnectedState || isCreateAnswerState;

    if (isRingingState) {
      // Truly incoming (not yet answered): show incoming UI + auto-answer to resume.
      this.incomingSession = this.createSession(call, 'incoming');
      this.activeSession = null;
      // Do not start the ringtone here. After a hard refresh the SDK can replay a
      // ringing invite for a few hundred ms, which was the iPhone "one ring then
      // fine" bug. We auto-answer below; a live new invite still rings via handleIncoming.

      // Auto-answer after refresh to restore the media negotiation.
      setTimeout(() => {
        try {
          // Only answer if the same incoming session is still present.
          if (!this.incomingSession || this.incomingSession.cleaned) return;
          if (this.incomingSession.callId !== call?.callId) return;

          const expectedCallId = call?.callId;

          // Only answer first; then do a soft recovery check in case answer() "succeeds"
          // but SDP/ICE is still effectively stuck after refresh.
          void this.answerCall()
            .then(() => {
              // Wait for normal SDP flow first.
              this.scheduleSoftRecovery(expectedCallId, 1500);
              // Start watchdog to cover hard-refresh mid-negotiation cases.
              startWatchdog(call, 8000);
            })
            .catch((err) => this.log('hydrate answerCall', err));
        } catch (err) {
          this.log('hydrate incoming ring branch', err);
        }
      }, 300);
    } else if (isOngoingState) {
      // Already answering / create_answer / connecting / connected: restore as an active call.
      this.activeSession = this.createSession(call, 'restored');
      this.incomingSession = null;
      if (isCreateAnswerState) {
        this.scheduleSoftRecovery(call?.callId, 2000);
      }
      startWatchdog(call, 8000);
    } else {
      return;
    }

    this.emit();
  }

  emit() {
    this.onStateChange?.({
      activeCall: this.activeSession?.call ?? null,
      incomingCall: this.incomingSession?.call ?? null,
      callError: this.callError,
      lastCallEndedReason: this.lastCallEndedReason,
      lastCallHangupParty: this.lastCallHangupParty,
    });
  }

  attemptIceRecovery = (call, session, { maxRetries = 5, watchdogMs = 32000, onTerminate } = {}) => {
    if (!call || call.callHasEnded?.()) {
      onTerminate?.();
      return;
    }
    if (session.iceRetries >= maxRetries) {
      onTerminate?.();
      return;
    }

    session.iceRetries += 1;
    const callId = call.callId;
    this.engine.createCallEntry(call, session.direction);
    this.engine.attemptIceRecovery(callId, {
      maxRetries,
      watchdogMs,
      onTerminate: (failedCall) => {
        if (!failedCall || failedCall.callHasEnded?.()) return;
        onTerminate?.(failedCall);
      },
    });
  };

  clearError = () => {
    this.callError = null;
    this.emit();
  };

  /**
   * Surface a media-access failure without starting Matrix VoIP
   * (e.g. gesture prewarm failed → show Meet-style Allow access UI).
   */
  reportMediaError = (error) => {
    this.callError = error || {
      code: CallErrorCode.NoUserMedia,
      message: 'Microphone/camera unavailable',
      mediaKind: 'permission',
    };
    this.emit();
  };

  clearLastCallEndState = () => {
    if (this.lastCallEndedReason == null && this.lastCallHangupParty == null) return;
    this.lastCallEndedReason = null;
    this.lastCallHangupParty = null;
    this.emit();
  };

  handleIncoming = (call) => {
    if (!call || call.callHasEnded?.()) return;

    const callId = call?.callId ?? null;
    if (this.isRecentlyTerminated(callId)) return;

    // If already restored/known → ignore duplicate incoming
    if (callId && this.activeSession?.callId === callId) {
      return;
    }

    if (callId && this.sessions.has(callId)) {
      const existingSession = this.sessions.get(callId);
      if (existingSession && !existingSession.cleaned) return;
      this.sessions.delete(callId);
    }

    const state = String(call?.state ?? '').toLowerCase();
    const ringingLower = this.callStateStrings.ringing;
    const createAnswerLower = this.callStateStrings.createAnswer;
    const connectingLower = this.callStateStrings.connecting;
    const connectedLower = this.callStateStrings.connected;

    const isRingingState = state === 'ringing' || state === ringingLower;
    const isCreateAnswerState = state === 'create_answer' || state === createAnswerLower;
    const isConnectingState = state === 'connecting' || state === connectingLower;
    const isConnectedState = state === 'connected' || state === connectedLower;
    const isOngoingState =
      isConnectingState || isConnectedState || isCreateAnswerState;

    // After hard refresh the SDK can sometimes emit `Call.incoming` even when the
    // call is already answering/connecting/connected. In that case, do NOT
    // flip the UI into "incoming" mode; restore it as an active call instead.
    if (isOngoingState) {
      // If we already have another in-progress call, reject this one.
      if (this.activeSession && !this.activeSession.cleaned) {
        try {
          if (this.activeSession.callId !== callId) this.safeReject?.(call);
        } catch (err) {
          this.log('handleIncoming safeReject (active)', err);
        }
        return;
      }
      if (this.incomingSession && !this.incomingSession.cleaned) {
        try {
          if (this.incomingSession.callId !== callId) this.safeReject?.(call);
        } catch (err) {
          this.log('handleIncoming safeReject (incoming)', err);
        }
        return;
      }

      this.lastCallEndedReason = null;
      this.lastCallHangupParty = null;
      this.callError = null;

      this.activeSession = this.createSession(call, 'restored');
      this.incomingSession = null;

      this.emit();
      if (isCreateAnswerState) {
        this.scheduleSoftRecovery(callId, 2000);
      }
      return;
    }

    // Not ringing and not an ongoing state we can restore -> ignore.
    if (!isRingingState) return;

    // If already in active call → reject new one
    if (this.activeSession && !this.activeSession.cleaned) {
      try {
        this.safeReject?.(call);
      } catch (err) {
        this.log('handleIncoming safeReject new vs active', err);
      }
      return;
    }

    // If we already have an unhandled incoming call → reject the new one
    if (this.incomingSession && !this.incomingSession.cleaned) {
      try {
        this.safeReject?.(call);
      } catch (err) {
        this.log('handleIncoming safeReject new vs incoming', err);
      }
      return;
    }

    this.lastCallEndedReason = null;
    this.lastCallHangupParty = null;
    this.callError = null;

    const session = this.createSession(call, 'incoming');
    this.incomingSession = session;
    this.emit();
    this.queueIncomingRingtone(callId);
  }

  clearPendingIncomingRingtone(callId) {
    if (!callId) return;
    const pending = this.pendingIncomingRingtoneByCallId.get(callId);
    if (pending) clearTimeout(pending);
    this.pendingIncomingRingtoneByCallId.delete(callId);
  }

  /**
   * Timeline is caught up. Keep a short hangup-follow window for replayed invites,
   * then stop delaying so live calls ring immediately.
   */
  markIncomingRingtoneCatchupComplete() {
    if (this.incomingRingtoneDelayUntil <= Date.now()) return;
    this.incomingRingtoneDelayUntil = Math.min(
      this.incomingRingtoneDelayUntil,
      Date.now() + RINGTONE_HANGUP_FOLLOW_MS
    );
    this.reschedulePendingIncomingRingtones();
  }

  reschedulePendingIncomingRingtones() {
    const wait = Math.max(0, this.incomingRingtoneDelayUntil - Date.now());
    const callIds = [...this.pendingIncomingRingtoneByCallId.keys()];
    callIds.forEach((callId) => {
      this.clearPendingIncomingRingtone(callId);
      if (wait === 0) {
        this.startIncomingRingtoneIfStillRinging(callId);
        return;
      }
      const timeoutId = setTimeout(() => this.startIncomingRingtoneIfStillRinging(callId), wait);
      this.pendingIncomingRingtoneByCallId.set(callId, timeoutId);
    });
  }

  startIncomingRingtoneIfStillRinging(callId) {
    this.clearPendingIncomingRingtone(callId);
    if (this.hydrateCancelled) return;
    if (!this.incomingSession || this.incomingSession.cleaned) return;
    if (callId && this.incomingSession.callId !== callId) return;
    const call = this.incomingSession.call;
    if (!call || call.callHasEnded?.()) return;
    const state = String(call.state ?? '').toLowerCase();
    if (state !== 'ringing' && state !== this.callStateStrings.ringing) return;
    startCallRingtoneLoop(CALL_RINGTONE_VARIANT.INCOMING);
  }

  /**
   * After a hard refresh, Matrix can emit Call.incoming for a timeline invite that is
   * immediately followed by hangup in the next sync. Delay the ringtone only while
   * catch-up is still in progress. Live invites after timeline is ready ring immediately.
   */
  queueIncomingRingtone(callId) {
    if (!callId) {
      this.startIncomingRingtoneIfStillRinging(null);
      return;
    }

    this.clearPendingIncomingRingtone(callId);

    const wait = Math.max(0, this.incomingRingtoneDelayUntil - Date.now());
    if (wait === 0) {
      this.startIncomingRingtoneIfStillRinging(callId);
      return;
    }

    const timeoutId = setTimeout(() => this.startIncomingRingtoneIfStillRinging(callId), wait);
    this.pendingIncomingRingtoneByCallId.set(callId, timeoutId);
  }

  createSession(call, direction) {
    if (call?.callId && this.sessions.has(call.callId)) {
      const existing = this.sessions.get(call.callId);
      if (existing && !existing.cleaned) return existing;
      // Session exists but is already cleaned; allow re-creating.
      this.sessions.delete(call.callId);
    }

    const session = new CallSession({
      call,
      direction,
      engine: this.engine,
      optimizeAudio: this.optimizeAudio,
      safeHangup: this.safeHangup,
      safeReject: this.safeReject,
      ensureCallEventHandler: this.ensureCallEventHandler,
      getFailureCategory: this.getFailureCategory,
      callStats: this.callStats,
      attemptIceRecovery: this.attemptIceRecovery,
      onUpdate: (payload) => {
        if (payload?.error) this.callError = payload.error;
        const state = String(payload?.state ?? '').toLowerCase();
        if (state === this.callStateStrings.connected) {
          const callId =
            payload?.callId ??
            this.activeSession?.callId ??
            this.incomingSession?.callId ??
            null;
          this.clearRecovery(callId);
          // Connected media path — stop ring/ringback (SDK feed warnings are unrelated).
          stopCallRingtoneLoop({ restoreSession: false });
        }
        this.emit();
      },
      onReplaced: ({ session: replacedSession, oldCall, oldCallId, newCall }) => {
        if (oldCallId) {
          // Call replacement should clear any in-flight soft recovery dedupe.
          // Otherwise, the old callId can permanently block recovery for the new call.
          this.clearRecovery(oldCallId);

          if (this.sessions.get(oldCallId) === replacedSession) {
            this.sessions.delete(oldCallId);
          }
        }
        if (newCall?.callId) {
          this.sessions.set(newCall.callId, replacedSession);
          this.clearRecovery(newCall.callId);
        }
        replacedSession.forceStopMediaFallback?.(oldCall);
        this.emit();
      },
      onEnd: this.onSessionEnd,
    });

    const peerConn = call?.peerConn;
    const onIceConnectionStateChange = () => {
      try {
        const iceState = call?.peerConn?.iceConnectionState;
        const callId = call?.callId;
        if (!callId) return;

        this.log('ICE state change', { callId, iceState });

        if (iceState === 'connected' || iceState === 'completed') {
          const pending = this.iceRefreshDebounceByCallId.get(callId);
          if (pending) clearTimeout(pending);
          this.iceRefreshDebounceByCallId.delete(callId);
          this.clearRecovery(callId);
          return;
        }

        // Debounced TURN refresh + ICE restart (shared with recovery ladder).
        if (iceState === 'failed' || iceState === 'disconnected') {
          this.triggerIceRestart(call);
        }
      } catch (err) {
        this.log('ICE state listener', err);
      }
    };
    peerConn?.addEventListener?.('iceconnectionstatechange', onIceConnectionStateChange);
    session._peerConn = peerConn || null;
    session._onIceConnectionStateChange = onIceConnectionStateChange;

    if (call?.callId) {
      this.sessions.set(call.callId, session);
    }
    this.callStats.started += 1;
    return session;
  }

  async placeCall(roomId, type) {
    if (this.activeSession && !this.activeSession.cleaned) return null;
    if (!roomId || typeof roomId !== 'string') return null;

    // Call Again while an invite is still live used to start a second ringtone
    // on top of the incoming one. Same room → answer. Other room → reject first.
    if (this.incomingSession && !this.incomingSession.cleaned) {
      const incomingRoomId = this.incomingSession.call?.roomId;
      if (incomingRoomId && incomingRoomId === roomId) {
        const media = await ensureCallMediaReady(type);
        if (!media.ok) {
          this.reportMediaError(media.error);
          return null;
        }
        return this.answerCall();
      }
      this.rejectCall();
    }

    // Meet/Zoom gate: never create a Matrix invite until local media is live.
    const media = await ensureCallMediaReady(type);
    if (!media.ok) {
      this.reportMediaError(media.error);
      return null;
    }

    const call = this.client?.createCall?.(roomId);
    if (!call) {
      this.callError = { code: null, message: 'Browser or client does not support calling' };
      this.emit();
      return null;
    }

    this.callError = null;
    this.lastCallEndedReason = null;
    this.lastCallHangupParty = null;

    const session = this.createSession(call, 'outgoing');
    this.activeSession = session;
    // Sync activeCall into React before async place() so stale lastCallEndedReason
    // from a prior call cannot trigger missed-call notices while ringing.
    this.emit();
    startCallRingtoneLoop(CALL_RINGTONE_VARIANT.OUTGOING);

    try {
      await session.place(type);
      this.emit();
      const placed = session.call;
      if (placed?.callId) {
        this.scheduleSoftRecovery(placed.callId, 2000);
      }
      return placed;
    } catch (e) {
      const message = e?.message || 'Failed to start call';
      this.callError = { code: null, message };
      stopCallRingtoneLoop({ restoreSession: true });
      session.hangup(CallErrorCode.UserHangup);
      this.emit();
      return null;
    }
  }

  async answerCall() {
    if (!this.incomingSession) return null;
    this.clearPendingIncomingRingtone(this.incomingSession.callId);

    // Media must already be acquired under the Answer tap (bootstrapIncomingCallAnswer).
    // Re-check so we never answer into a guaranteed NoUserMedia hangup.
    const callKind =
      this.incomingSession.isVideoCall?.() || this.incomingSession.call?.type === 'video'
        ? 'video'
        : 'voice';
    const media = await ensureCallMediaReady(callKind);
    if (!media.ok) {
      this.reportMediaError(media.error);
      return null;
    }

    // Stop incoming ring before negotiation (keep PlayAndRecord for mic).
    stopCallRingtoneLoop({ restoreSession: false });

    const session = this.incomingSession;
    this.incomingSession = null;
    this.activeSession = session;
    this.callError = null;
    // Publish activeCall before async negotiation so /call route can mount immediately.
    this.emit();

    try {
      await session.answer();
      this.emit();
      return session.call;
    } catch (e) {
      this.callError = { code: null, message: e?.message || 'Failed to answer call' };
      session.hangup(CallErrorCode.UserHangup);
      this.emit();
      return null;
    }
  }

  rejectCall() {
    if (!this.incomingSession) return;
    const callId = this.incomingSession.callId;
    if (callId && this.shouldSkipDuplicateTerminate(callId)) return;
    this.markCallRecentlyTerminated(callId);
    this.clearPendingIncomingRingtone(callId);
    stopCallRingtoneLoop({ restoreSession: true });
    unlockCallRingtoneAudio();
    this.incomingSession.reject();
    this.incomingSession = null;
    this.emit();
  }

  /**
   * @param {string} [endedReason] — matrix-js-sdk {@link CallErrorCode} or app reason (e.g. insufficient_credits)
   */
  endCall(endedReason = CallErrorCode.UserHangup) {
    if (this.activeSession) {
      const callId = this.activeSession.callId;
      if (callId && this.shouldSkipDuplicateTerminate(callId)) return;
      this.markCallRecentlyTerminated(callId);
      stopCallRingtoneLoop({ restoreSession: true });
      this.activeSession.hangup(endedReason);
      this.activeSession = null;
      unlockCallRingtoneAudio();
      this.emit();
      return;
    }
    if (this.incomingSession) {
      const callId = this.incomingSession.callId;
      if (callId && this.shouldSkipDuplicateTerminate(callId)) return;
      this.markCallRecentlyTerminated(callId);
      this.clearPendingIncomingRingtone(callId);
      stopCallRingtoneLoop({ restoreSession: true });
      this.incomingSession.reject();
      this.incomingSession = null;
      unlockCallRingtoneAudio();
      this.emit();
    }
  }

  onSessionEnd = (session, { endedReason = null, hangupParty = null } = {}) => {
    if (!session) return;
    this.clearPendingIncomingRingtone(session.callId);
    stopCallRingtoneLoop({ restoreSession: true });
    this.markCallRecentlyTerminated(session.callId);
    if (session.callId) {
      const pending = this.iceRefreshDebounceByCallId.get(session.callId);
      if (pending) clearTimeout(pending);
      this.iceRefreshDebounceByCallId.delete(session.callId);
    }
    try {
      session?._peerConn?.removeEventListener?.(
        'iceconnectionstatechange',
        session?._onIceConnectionStateChange
      );
    } catch (err) {
      this.log('onSessionEnd remove ice listener', err);
    }
    session._peerConn = null;
    session._onIceConnectionStateChange = null;
    this.sessions.delete(session.callId);
    if (this.activeSession === session) this.activeSession = null;
    if (this.incomingSession === session) this.incomingSession = null;
    if (session.callId) this.clearRecovery(session.callId);

    this.lastCallEndedReason = endedReason;
    this.lastCallHangupParty = hangupParty;
    releaseCallMediaCaptureCache();
    unlockCallRingtoneAudio();
    this.emit();
  };

  destroy() {
    this.hydrateCancelled = true;
    try {
      this.client?.off?.('Call.incoming', this.handleIncoming);
    } catch (err) {
      this.log('destroy off Call.incoming', err);
    }
    this.recoveryScheduledTtlByCallId.forEach((id) => {
      try {
        clearTimeout(id);
      } catch (err) {
        this.log('destroy recoveryScheduled ttl', err);
      }
    });
    this.recoveryScheduledTtlByCallId.clear();
    this.iceRefreshDebounceByCallId.forEach((id) => {
      try {
        clearTimeout(id);
      } catch (err) {
        this.log('destroy ice debounce', err);
      }
    });
    this.iceRefreshDebounceByCallId.clear();
    this.recentlyTerminatedCallIds.clear();
    this.pendingIncomingRingtoneByCallId.forEach((id) => {
      try {
        clearTimeout(id);
      } catch (err) {
        this.log('destroy pending incoming ringtone', err);
      }
    });
    this.pendingIncomingRingtoneByCallId.clear();
    for (const session of this.sessions.values()) {
      try {
        session?._peerConn?.removeEventListener?.(
          'iceconnectionstatechange',
          session?._onIceConnectionStateChange
        );
      } catch (err) {
        this.log('destroy remove ice listener', err);
      }
      try {
        session.cleanup();
      } catch (err) {
        this.log('destroy session.cleanup', err);
      }
    }
    this.sessions.clear();
    this.activeSession = null;
    this.incomingSession = null;
    this.callError = null;
  }
}
